import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaHotel, FaCheck, FaTimes, FaBed } from "react-icons/fa";
import { useHabitaciones } from "../../../services/hooks/useHabitaciones";
import { useModulos } from "../../../services/hooks/useModulos";
import { useZonas } from "../../../services/hooks/useZonas";

const MantenimientoHabitaciones = () => {
  const navigate = useNavigate();

  // Hooks para manejo de datos
  const {
    habitaciones,
    loading,
    error,
    fetchHabitacionesByModulo,
    createHabitacion,
    updateHabitacion,
    deleteHabitacion,
    clearError
  } = useHabitaciones();

  const { modulos, fetchModulos } = useModulos();
  const { zonas, fetchZonas } = useZonas();

  const [state, setState] = useState({
    zonaSeleccionada: null,
    moduloSeleccionado: null,
    filtro: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [habitacionToEdit, setHabitacionToEdit] = useState(null);
  const [formData, setFormData] = useState({
    numero: '',
    piso: 1,
    capacidad: 1,
    modulo_id: null,
    estado_habitacion_id: 1,
    observaciones: ''
  });

  // Cargar zonas al montar el componente
  useEffect(() => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
    if (selectedProject && selectedProject.id) {
      fetchZonas(selectedProject.id);
    }
  }, [fetchZonas]);

  // Filtrar habitaciones según el filtro
  const habitacionesFiltradas = habitaciones.filter((habitacion) =>
    habitacion.numero.toLowerCase().includes(state.filtro.toLowerCase()) ||
    habitacion.piso.toString().includes(state.filtro)
  );

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      numero: '',
      piso: 1,
      capacidad: 1,
      modulo_id: null,
      estado_habitacion_id: 1,
      observaciones: ''
    });
    setHabitacionToEdit(null);
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setFormData({
      numero: '',
      piso: 1,
      capacidad: 1,
      modulo_id: state.moduloSeleccionado?.id || null,
      estado_habitacion_id: 1,
      observaciones: ''
    });
    setHabitacionToEdit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = (habitacion) => {
    setModalMode('edit');
    setFormData({
      numero: habitacion.numero,
      piso: habitacion.piso,
      capacidad: habitacion.capacidad,
      modulo_id: habitacion.modulo_id,
      estado_habitacion_id: habitacion.estado_habitacion_id,
      observaciones: habitacion.observaciones || ''
    });
    setHabitacionToEdit(habitacion);
    setShowModal(true);
  };

  const handleSaveHabitacion = async () => {
    try {
      if (modalMode === 'create') {
        await createHabitacion(formData);
      } else {
        await updateHabitacion(habitacionToEdit.id, formData);
      }
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar habitación:', error);
    }
  };

  const handleDeleteHabitacion = async (habitacion) => {
    if (window.confirm(`¿Estás seguro de eliminar la habitación ${habitacion.numero}?`)) {
      try {
        await deleteHabitacion(habitacion.id);
      } catch (error) {
        console.error('Error al eliminar habitación:', error);
      }
    }
  };

  const handleZonaChange = async (zonaId) => {
    if (zonaId) {
      const zona = zonas.find(z => z.id === zonaId);
      setState({ ...state, zonaSeleccionada: zona, moduloSeleccionado: null });
      // Cargar módulos de la zona seleccionada
      await fetchModulos(zonaId);
    } else {
      setState({ ...state, zonaSeleccionada: null, moduloSeleccionado: null });
    }
  };

  const handleModuloChange = async (moduloId) => {
    if (moduloId) {
      const modulo = modulos.find(m => m.id === moduloId);
      setState({ ...state, moduloSeleccionado: modulo });
      await fetchHabitacionesByModulo(moduloId);
    } else {
      setState({ ...state, moduloSeleccionado: null });
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      1: { text: 'Disponible', variant: 'success' },
      2: { text: 'Ocupada', variant: 'danger' },
      3: { text: 'Averiada', variant: 'warning' },
      4: { text: 'Limpieza', variant: 'info' }
    };
    const estadoInfo = estados[estado] || { text: 'Desconocido', variant: 'secondary' };
    return <Badge bg={estadoInfo.variant}>{estadoInfo.text}</Badge>;
  };

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Habitaciones */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            background: white;
            border-radius: 20px;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
            border: none;
            overflow: hidden;
            transition: all 0.3s ease;
          }
          
          .dh-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
          }
          
          .dh-card-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-bottom: 2px solid #dee2e6;
            padding: 1.5rem;
            font-weight: 600;
            font-size: 1.2rem;
            color: #495057;
          }
          
          .dh-card-body {
            padding: 2rem;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border: none;
            border-radius: 12px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          }
          
          .btn-dh-primary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
            border: none;
            border-radius: 12px;
            padding: 0.75rem 1.5rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(108, 117, 125, 0.3);
          }
          
          .btn-dh-secondary:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(108, 117, 125, 0.4);
            color: white;
          }
          
          .btn-dh-success {
            background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
            border: none;
            border-radius: 12px;
            padding: 0.5rem 1rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
          }
          
          .btn-dh-success:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(40, 167, 69, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #dc3545 0%, #e83e8c 100%);
            border: none;
            border-radius: 12px;
            padding: 0.5rem 1rem;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3);
          }
          
          .btn-dh-danger:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(220, 53, 69, 0.4);
            color: white;
          }
          
          /* Formularios Digital Harmony */
          .dh-form-control {
            border: 2px solid #e9ecef;
            border-radius: 12px;
            padding: 0.75rem 1rem;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: #f8f9fa;
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Tabla Digital Harmony */
          .dh-table {
            background: white;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          }
          
          .dh-table thead th {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border: none;
            padding: 1rem;
            font-weight: 600;
            font-size: 0.95rem;
          }
          
          .dh-table tbody td {
            padding: 1rem;
            border-bottom: 1px solid #f1f3f4;
            vertical-align: middle;
          }
          
          .dh-table tbody tr:hover {
            background: #f8f9fa;
          }
          
          /* Modal Digital Harmony */
          .dh-modal .modal-content {
            border-radius: 20px;
            border: none;
            box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
          }
          
          .dh-modal .modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 20px 20px 0 0;
            border: none;
            padding: 1.5rem;
          }
          
          .dh-modal .modal-body {
            padding: 2rem;
          }
          
          .dh-modal .modal-footer {
            border: none;
            padding: 1.5rem 2rem;
            background: #f8f9fa;
            border-radius: 0 0 20px 20px;
          }
          
          /* Estados y badges */
          .dh-badge {
            padding: 0.5rem 1rem;
            border-radius: 20px;
            font-weight: 600;
            font-size: 0.85rem;
          }
          
          /* Animaciones */
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .dh-fade-in {
            animation: fadeInUp 0.6s ease-out;
          }
          
          /* Responsive */
          @media (max-width: 768px) {
            .dh-main-title {
              font-size: 1.8rem;
            }
            
            .dh-card-body {
              padding: 1.5rem;
            }
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        {/* Header Principal */}
        <div className="dh-main-header">
          <Container>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <Button
                    variant="outline-light"
                    className="me-3 btn-dh-secondary"
                    onClick={() => navigate('/mantenimientos')}
                  >
                    <FaArrowLeft className="me-2" />
                    Volver
                  </Button>
                  <div>
                    <h1 className="dh-main-title">
                      <FaHotel className="me-3" />
                      Mantenimiento de Habitaciones
                    </h1>
                    <p className="dh-main-subtitle">
                      Gestiona las habitaciones de los módulos hoteleros
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button
                  className="btn-dh-primary"
                  onClick={handleOpenCreateModal}
                  disabled={!state.moduloSeleccionado}
                >
                  <FaPlus className="me-2" />
                  Nueva Habitación
                </Button>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="dh-fade-in">
          {/* Filtros y Selección */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <FaSearch className="me-2" />
                  Filtros y Selección
                </Card.Header>
                <Card.Body className="dh-card-body">
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaHotel className="me-2" />
                          Seleccionar Zona *
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={state.zonaSeleccionada?.id || ''}
                          onChange={(e) => handleZonaChange(parseInt(e.target.value) || null)}
                        >
                          <option value="">Seleccione una zona...</option>
                          {zonas.map((zona) => (
                            <option key={zona.id} value={zona.id}>
                              {zona.nombre}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Seleccione una zona para ver sus módulos
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaHotel className="me-2" />
                          Seleccionar Módulo *
                        </Form.Label>
                        <Form.Select
                          className="dh-form-control"
                          value={state.moduloSeleccionado?.id || ''}
                          onChange={(e) => handleModuloChange(parseInt(e.target.value) || null)}
                          disabled={!state.zonaSeleccionada}
                        >
                          <option value="">Seleccione un módulo...</option>
                          {modulos
                            .filter(modulo => modulo.zona_id === state.zonaSeleccionada?.id)
                            .map((modulo) => (
                            <option key={modulo.id} value={modulo.id}>
                              {modulo.nombre} - {modulo.codigo}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Text className="text-muted">
                          Seleccione un módulo para ver sus habitaciones
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">
                          <FaSearch className="me-2" />
                          Buscar Habitación
                        </Form.Label>
                        <Form.Control
                          type="text"
                          className="dh-form-control"
                          placeholder="Buscar por número o piso..."
                          value={state.filtro}
                          onChange={(e) => setState({ ...state, filtro: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tabla de Habitaciones */}
          <Row>
            <Col md={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <FaBed className="me-2" />
                  Habitaciones
                  {state.moduloSeleccionado && (
                    <span className="ms-2 text-muted">
                      - {state.moduloSeleccionado.nombre}
                    </span>
                  )}
                </Card.Header>
                <Card.Body className="dh-card-body p-0">
                  {error && (
                    <Alert variant="danger" className="m-3">
                      {error}
                      <Button variant="outline-danger" size="sm" className="ms-2" onClick={clearError}>
                        Cerrar
                      </Button>
                    </Alert>
                  )}

                  {loading ? (
                    <div className="text-center p-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                      </div>
                      <p className="mt-2 text-muted">Cargando habitaciones...</p>
                    </div>
                  ) : !state.moduloSeleccionado ? (
                    <div className="text-center p-5">
                      <FaHotel size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">Seleccione una zona y módulo</h5>
                      <p className="text-muted">Para ver las habitaciones, primero seleccione una zona y luego un módulo</p>
                    </div>
                  ) : habitacionesFiltradas.length === 0 ? (
                    <div className="text-center p-5">
                      <FaBed size={48} className="text-muted mb-3" />
                      <h5 className="text-muted">No hay habitaciones</h5>
                      <p className="text-muted">
                        {state.filtro 
                          ? 'No se encontraron habitaciones con el filtro aplicado'
                          : 'Este módulo no tiene habitaciones registradas'
                        }
                      </p>
                      {!state.filtro && (
                        <Button className="btn-dh-primary" onClick={handleOpenCreateModal}>
                          <FaPlus className="me-2" />
                          Crear Primera Habitación
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="dh-table mb-0">
                        <thead>
                          <tr>
                            <th>Número</th>
                            <th>Piso</th>
                            <th>Capacidad</th>
                            <th>Estado</th>
                            <th>Camas</th>
                            <th>Observaciones</th>
                            <th width="150">Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {habitacionesFiltradas.map((habitacion) => (
                            <tr key={habitacion.id}>
                              <td>
                                <strong>{habitacion.numero}</strong>
                              </td>
                              <td>
                                <Badge bg="info" className="dh-badge">
                                  Piso {habitacion.piso}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg="primary" className="dh-badge">
                                  {habitacion.capacidad} personas
                                </Badge>
                              </td>
                              <td>
                                {getEstadoBadge(habitacion.estado_habitacion_id)}
                              </td>
                              <td>
                                <Badge bg="secondary" className="dh-badge">
                                  {habitacion.total_camas || 0} camas
                                </Badge>
                              </td>
                              <td>
                                <span className="text-muted">
                                  {habitacion.observaciones || 'Sin observaciones'}
                                </span>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="btn-dh-success"
                                    onClick={() => handleOpenEditModal(habitacion)}
                                    title="Editar habitación"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    className="btn-dh-danger"
                                    onClick={() => handleDeleteHabitacion(habitacion)}
                                    title="Eliminar habitación"
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal para Crear/Editar Habitación */}
        {showModal && (
          <div className="modal show d-block dh-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <FaBed className="me-2" />
                    {modalMode === 'create' ? 'Nueva Habitación' : 'Editar Habitación'}
                  </h5>
                  <button type="button" className="btn-close btn-close-white" onClick={handleCloseModal}></button>
                </div>
                <div className="modal-body">
                  <Form onSubmit={(e) => { e.preventDefault(); handleSaveHabitacion(); }}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Número de Habitación *
                          </Form.Label>
                          <Form.Control
                            type="text"
                            className="dh-form-control"
                            placeholder="Ej: 101, A-01, B-02"
                            value={formData.numero || ''}
                            onChange={(e) => setFormData({...formData, numero: e.target.value})}
                            required
                            maxLength={10}
                          />
                          <Form.Text className="text-muted">
                            Número único de la habitación (máximo 10 caracteres)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Piso *
                          </Form.Label>
                          <Form.Control
                            type="number"
                            className="dh-form-control"
                            placeholder="Ej: 1, 2, 3"
                            value={formData.piso || ''}
                            onChange={(e) => setFormData({...formData, piso: parseInt(e.target.value) || 1})}
                            required
                            min="1"
                            max="20"
                          />
                          <Form.Text className="text-muted">
                            Piso donde se encuentra la habitación (1-20)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Capacidad *
                          </Form.Label>
                          <Form.Control
                            type="number"
                            className="dh-form-control"
                            placeholder="Ej: 1, 2, 4"
                            value={formData.capacidad || ''}
                            onChange={(e) => setFormData({...formData, capacidad: parseInt(e.target.value) || 1})}
                            required
                            min="1"
                            max="8"
                          />
                          <Form.Text className="text-muted">
                            Capacidad máxima de personas (1-8)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Estado
                          </Form.Label>
                          <Form.Select
                            className="dh-form-control"
                            value={formData.estado_habitacion_id || 1}
                            onChange={(e) => setFormData({...formData, estado_habitacion_id: parseInt(e.target.value)})}
                          >
                            <option value={1}>Disponible</option>
                            <option value={2}>Ocupada</option>
                            <option value={3}>Averiada</option>
                            <option value={4}>Limpieza</option>
                          </Form.Select>
                          <Form.Text className="text-muted">
                            Estado actual de la habitación
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaBed className="me-2" />
                            Observaciones
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            className="dh-form-control"
                            placeholder="Observaciones adicionales sobre la habitación..."
                            value={formData.observaciones || ''}
                            onChange={(e) => setFormData({...formData, observaciones: e.target.value})}
                          />
                          <Form.Text className="text-muted">
                            Notas adicionales sobre la habitación (opcional)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            <FaHotel className="me-2" />
                            Módulo
                          </Form.Label>
                          <Form.Control
                            type="text"
                            className="dh-form-control"
                            value={state.moduloSeleccionado?.nombre || ''}
                            disabled
                            readOnly
                          />
                          <Form.Text className="text-muted">
                            La habitación pertenece al módulo seleccionado
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </div>
                <div className="modal-footer">
                  <Button className="btn-dh-secondary" onClick={handleCloseModal}>
                    <FaTimes className="me-2" />
                    Cancelar
                  </Button>
                  <Button 
                    className="btn-dh-primary" 
                    onClick={handleSaveHabitacion}
                    disabled={!formData.numero || !formData.modulo_id || formData.capacidad < 1}
                  >
                    <FaCheck className="me-2" />
                    {modalMode === 'create' ? 'Crear Habitación' : 'Actualizar Habitación'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </div>
    </>
  );
};

export default MantenimientoHabitaciones;